/**
 * Per-planet settlement TierRegistry (W2c-1) — the generation core of the
 * adopted settlement-tier spec (docs/superpowers/specs/2026-07-07-settlement-tiers-design.md).
 *
 * Every region cell's jittered-subgrid candidates (same geometry/salts as the
 * legacy settlementSeeds) are enumerated once, ranked by suitability, and dealt
 * settlement TIERS from the planet's quota deck deterministically:
 *
 *   metropolis (5) 0–1 · cities (4) 2–3 · towns (3) 6–10 · villages (2) 15–25 ·
 *   hamlets (1) 30+  →  ~60–100 settlements per full terran planet.
 *
 * Waystations (0) are NOT quota-dealt: a sparse per-cell hash roll that fires on
 * ANY archetype (the habitability carve-out — infrastructure, not civilization),
 * so barren/ice/volcanic/desert worlds still get save/refuel points. On habitable
 * planets they only land in cells with no quota settlement.
 *
 * The registry is built once per PlanetSampler (WeakMap cache, the rivers/towns
 * lazy-build pattern) and is a PURE function of the planet def + the pure fields,
 * so every worker/engine rebuilds an identical deck (verified by the determinism
 * digests). settlementSeeds() is a thin view over byCell.
 */
import type { PlanetSampler } from "../universe.js";
import { hashInts, h01 } from "./hash.js";
import { type CellId, cellsPerFace, LEVEL_REGION, packCell } from "./cells.js";
import { hashSeed } from "../rng.js";
import { areaSuitabilityAt, isWetAt, candidateDir, steepFracAt, SETTLEMENT_SUB } from "./fields.js";

// Tier constants (spec §"The six tiers").
export const TIER_WAYSTATION = 0;
export const TIER_HAMLET = 1;
export const TIER_VILLAGE = 2;
export const TIER_TOWN = 3;
export const TIER_CITY = 4;
export const TIER_METROPOLIS = 5;

/** Display names indexed by tier 0..5 — the canonical census/warp/debug labels. */
export const TIER_NAMES = ["waystation", "hamlet", "village", "town", "city", "metropolis"] as const;

export interface TierAssignment {
  /** packCell of the owning region cell. */
  cellKey: number;
  /** Subcell index (0..SUB²-1) — with cellKey addresses the candidate geometry. */
  k: number;
  /** Settlement tier 0..5 (see TIER_* constants). */
  tier: number;
  /** 0 = no rule-break; else a hash-rolled deviation id 1..4 (data-only, W2c-1). */
  deviation: number;
  /** Suitability at the candidate — cached so settlementSeeds needn't resample. */
  suit: number;
}

export interface TierRegistry {
  /** Region cellKey → its dealt assignments (waystation + quota). */
  byCell: Map<number, TierAssignment[]>;
  /** counts[tier] = number of settlements dealt at that tier (length 6). */
  counts: number[];
}

// ── tuning knobs (spec ratios) ───────────────────────────────────────────────
const SUIT_MIN = 0.02;              // candidate floor (matches legacy settlementSeeds)
const BUILD_PROBE_RADIUS_M = 260;   // buildability disc (village/town/city core reach)
const BUILD_MAX_STEEP_FRAC = 0.30;  // measured: healthy sites <=0.29, broken >=0.31
const FULL_REF = 100;               // candidate pool that earns undiminished quotas
const METRO_MIN_CANDIDATES = 60;    // metropolis only where the planet supports scale
const METRO_PROB = 0.35;            // ~35% of qualifying planets bear a metropolis
const WAYSTATION_RATE = 0.02;       // ~0.02 waystations per cell (any archetype)

// Salts (registered in hash.ts): all per-PLANET rolls (keyed on planetHash) except
// deviation/waystation which key on the cell.
const SALT_METRO = 0x3e7a;   // metropolis presence gate
const SALT_QUOTA = 0x9074;   // city/town/village/hamlet count rolls (3rd arg = tier)
const SALT_DEV = 0xde1a;     // per-settlement deviation slot
const SALT_WAY = 0x3a71;     // waystation presence (f=0) + placement subcell (f=1)
const SALT_WAYRETRY = 0x7a91; // waystation dry-slot re-roll: fresh subcell per attempt (f=attempt)

const cache = new WeakMap<PlanetSampler, TierRegistry>();

/** One deviation roll (data-only this phase): <0.15 → id 1..4, else 0. */
function rollDeviation(planetHash: number, cellKey: number, k: number): number {
  if (h01(hashInts(planetHash, SALT_DEV, cellKey, k)) >= 0.15) return 0;
  return 1 + Math.floor(h01(hashInts(planetHash, SALT_DEV, cellKey, k, 1)) * 4);
}

interface Candidate { cell: CellId; cellKey: number; k: number; suit: number; }

/**
 * Build (or fetch the cached) TierRegistry for `sampler`. See the module header.
 * Cost note: on a habitable planet this evaluates suitabilityAt for every region
 * subcell once (the expensive leg); non-habitable planets skip straight to the
 * cheap per-cell waystation roll.
 */
export function tierRegistryFor(sampler: PlanetSampler): TierRegistry {
  const hit = cache.get(sampler);
  if (hit !== undefined) return hit;
  const reg = buildTierRegistry(sampler);
  cache.set(sampler, reg);
  return reg;
}

function buildTierRegistry(sampler: PlanetSampler): TierRegistry {
  const planetHash = hashSeed(sampler.def.seed);
  const arch = sampler.def.archetype;
  const habitable = arch === "terran" || arch === "ocean";
  const n = cellsPerFace(sampler.def.radius, LEVEL_REGION);
  const SUBN = SETTLEMENT_SUB * SETTLEMENT_SUB; // referenced lazily (import cycle)
  const byCell = new Map<number, TierAssignment[]>();
  const counts = [0, 0, 0, 0, 0, 0];
  const push = (a: TierAssignment): void => {
    let arr = byCell.get(a.cellKey);
    if (arr === undefined) { arr = []; byCell.set(a.cellKey, arr); }
    arr.push(a);
    counts[a.tier]++;
  };

  // ── 1. Quota deal (habitable planets only) ─────────────────────────────────
  // Non-habitable worlds get NO tier ≥ hamlet — waystations only (carve-out).
  const assignedCells = new Set<number>();
  if (habitable) {
    const cands: Candidate[] = [];
    const dir: number[] = [0, 0, 0];
    for (let face = 0; face < 6; face++) {
      for (let x = 0; x < n; x++) {
        for (let y = 0; y < n; y++) {
          const cell: CellId = { face, x, y, level: LEVEL_REGION };
          const cellKey = packCell(cell);
          for (let k = 0; k < SUBN; k++) {
            candidateDir(planetHash, cell, n, k, dir);
            const suit = areaSuitabilityAt(sampler, dir[0], dir[1], dir[2]);
            if (suit <= SUIT_MIN) continue;
            cands.push({ cell, cellKey, k, suit });
          }
        }
      }
    }
    // Rank: suitability desc, then cellKey asc, then k asc (index tie-breaks).
    cands.sort((a, b) => (b.suit - a.suit) || (a.cellKey - b.cellKey) || (a.k - b.k));

    const N = cands.length;
    const scale = Math.min(1, N / FULL_REF); // small-planet proportional scaling
    const roll = (i: number): number => h01(hashInts(planetHash, SALT_QUOTA, i));
    // budget[tier]: rolled within the spec range, then scaled down on small
    // planets. Metropolis needs a qualifying candidate pool AND wins its gate.
    const budget = [0, 0, 0, 0, 0, 0];
    budget[TIER_HAMLET] = Math.round((30 + Math.floor(roll(4) * 31)) * scale); // 30..60
    budget[TIER_VILLAGE] = Math.round((15 + Math.floor(roll(3) * 11)) * scale); // 15..25
    budget[TIER_TOWN] = Math.round((6 + Math.floor(roll(2) * 5)) * scale);      // 6..10
    budget[TIER_CITY] = Math.round((2 + Math.floor(roll(1) * 2)) * scale);      // 2..3
    budget[TIER_METROPOLIS] =
      N >= METRO_MIN_CANDIDATES && h01(hashInts(planetHash, SALT_METRO, 0)) < METRO_PROB ? 1 : 0;

    const dealt: Candidate[] = [];
    const dealtTier: number[] = [];
    // Spacing: a dealt tier ≥ TOWN must sit ≥ 2 cells (Chebyshev, same face) from
    // any equal-or-higher-tier settlement already dealt. Skip-down = try the next
    // lower tier for this candidate, then the next candidate for the budget.
    const tooClose = (cand: Candidate, tier: number): boolean => {
      for (let i = 0; i < dealt.length; i++) {
        if (dealtTier[i] < tier) continue;
        const o = dealt[i].cell;
        if (o.face !== cand.cell.face) continue;
        if (Math.max(Math.abs(o.x - cand.cell.x), Math.abs(o.y - cand.cell.y)) < 2) return true;
      }
      return false;
    };
    // Buildability: a dealt tier ≥ VILLAGE must sit on ground the street grower
    // can actually use. areaSuitabilityAt scores macro slope (200 m continentalH
    // FD) and misses building-scale ridge detail — a mountain flank can rank
    // near-top yet be >40% unbuildable, which starves growth into braided
    // contour strands (found live: 3:-1:2 Voxes cell (5,5,7), a tier-4 city with
    // 68 parcels). Gate on the measured share of the settlement disc above the
    // street grade wall; failures skip down (hamlet lanes are welcome on hills).
    // Memoized per candidate — the probe is ~230 terrain samples.
    const steepMemo = new Map<number, number>();
    const tooSteep = (cand: Candidate, ci: number): boolean => {
      let sf = steepMemo.get(ci);
      if (sf === undefined) {
        candidateDir(planetHash, cand.cell, n, cand.k, dir);
        sf = steepFracAt(sampler, dir[0], dir[1], dir[2], BUILD_PROBE_RADIUS_M);
        steepMemo.set(ci, sf);
      }
      return sf > BUILD_MAX_STEEP_FRAC;
    };
    let remaining = budget[1] + budget[2] + budget[3] + budget[4] + budget[5];
    for (let ci = 0; ci < N && remaining > 0; ci++) {
      const cand = cands[ci];
      for (let tier = TIER_METROPOLIS; tier >= TIER_HAMLET; tier--) {
        if (budget[tier] <= 0) continue;
        if (tier >= TIER_TOWN && tooClose(cand, tier)) continue; // skip down
        if (tier >= TIER_VILLAGE && tooSteep(cand, ci)) continue; // skip down
        dealt.push(cand);
        dealtTier.push(tier);
        budget[tier]--;
        remaining--;
        assignedCells.add(cand.cellKey);
        push({ cellKey: cand.cellKey, k: cand.k, tier, deviation: rollDeviation(planetHash, cand.cellKey, cand.k), suit: cand.suit });
        break;
      }
    }

    // C4 home capital guarantee: a `home` planet always bears at least one
    // TOWN — the roadmap's promised capital. The normal deal usually delivers
    // (terran, temperate, quota towns 6–10); this fallback covers small quota
    // rolls and gate skips: the best buildable candidate not already dealt a
    // town+ gets one (steep gate respected; waived only if EVERY candidate is
    // steep — a practical impossibility on a forced-terran world).
    if (sampler.def.home === true && counts[TIER_TOWN] + counts[TIER_CITY] + counts[TIER_METROPOLIS] === 0) {
      const taken = new Set(dealt); // a candidate already dealt a lower tier keeps it
      let pick = -1;
      for (let ci = 0; ci < N; ci++) {
        if (taken.has(cands[ci])) continue;
        if (!tooSteep(cands[ci], ci)) { pick = ci; break; }
      }
      if (pick < 0) for (let ci = 0; ci < N; ci++) { if (!taken.has(cands[ci])) { pick = ci; break; } }
      if (pick >= 0) {
        const cand = cands[pick];
        assignedCells.add(cand.cellKey);
        push({ cellKey: cand.cellKey, k: cand.k, tier: TIER_TOWN, deviation: rollDeviation(planetHash, cand.cellKey, cand.k), suit: cand.suit });
      }
    }
  }

  // ── 2. Waystations (all archetypes) ────────────────────────────────────────
  // Sparse per-cell roll. On habitable planets, only cells with no quota
  // settlement are eligible; on non-habitable planets every cell is.
  const wdir: number[] = [0, 0, 0];
  for (let face = 0; face < 6; face++) {
    for (let x = 0; x < n; x++) {
      for (let y = 0; y < n; y++) {
        const cell: CellId = { face, x, y, level: LEVEL_REGION };
        const cellKey = packCell(cell);
        if (habitable && assignedCells.has(cellKey)) continue;
        if (h01(hashInts(planetHash, SALT_WAY, face, x, y)) >= WAYSTATION_RATE) continue;
        // Dry-slot validation (W2d-2 rider 2): if the dealt subcell lands in open
        // water, re-roll a fresh subcell up to 4 times (new salt, per attempt);
        // still wet → skip the deal (no waystation on this cell).
        let k = Math.floor(h01(hashInts(planetHash, SALT_WAY, face, x, y, 1)) * SUBN);
        candidateDir(planetHash, cell, n, k, wdir);
        for (let attempt = 1; attempt <= 4 && isWetAt(sampler, wdir[0], wdir[1], wdir[2]); attempt++) {
          k = Math.floor(h01(hashInts(planetHash, SALT_WAYRETRY, face, x, y, attempt)) * SUBN);
          candidateDir(planetHash, cell, n, k, wdir);
        }
        if (isWetAt(sampler, wdir[0], wdir[1], wdir[2])) continue;
        const suit = areaSuitabilityAt(sampler, wdir[0], wdir[1], wdir[2]);
        push({ cellKey, k, tier: TIER_WAYSTATION, deviation: rollDeviation(planetHash, cellKey, k), suit });
      }
    }
  }

  return { byCell, counts };
}
